// =============================================================================
// participations-client.http.ts — Adaptateur HTTP RÉEL du ParticipationsClient (T2-REGISTRE).
// =============================================================================
// `fetch` contre `POST /admin/campaigns/participations/summary` (BC campaign, AdminSessionGuard).
// Substitué au stub derrière le MÊME DI seam (ParticipationsClientProvider). L'AUTORITÉ est l'API :
// cet adaptateur sérialise la demande, parse la réponse, relaie le message d'erreur serveur.
//
// SÉCURITÉ (RULE_REF_006, parité HttpSupplierClient) : session admin = cookie httpOnly
// (`credentials: 'include'`). CSRF DOUBLE-SUBMIT : le POST ré-émet le cookie NON-httpOnly `admin_csrf`
// en en-tête `x-csrf-token` (AdminSessionGuard l'exige sur les mutations ; sans lui = 403). Aucune
// donnée métier en localStorage. CHUNKING (GATE D) : au-delà de MAX_SUMMARY_SIRETS, la demande est
// découpée en lots bornés puis fusionnée (les appels non-mutants sont parallélisables).

import {
  MAX_SUMMARY_SIRETS,
  ParticipationsApiError,
  type ParticipationsClient,
  type SupplierOpenCampaignFlagDto,
  type SupplierParticipationsBySiretDto,
  type SupplierParticipationSummaryDto,
} from '@/api/participations-client';

/** Forme attendue d'un corps d'erreur structuré (NestJS HttpException `{ statusCode, message }`). */
interface StructuredErrorBody {
  readonly statusCode?: number;
  readonly message?: string;
}

/** Nom du cookie CSRF NON-httpOnly posé au login admin (double-submit, cf. API CSRF_COOKIE). */
const CSRF_COOKIE_NAME = 'admin_csrf';

/**
 * Lit la valeur du cookie CSRF `admin_csrf` (NON-httpOnly, lisible par le SPA même origine).
 * `undefined` si absent (pas de session, ou contexte sans document). Parité stricte avec les autres
 * adaptateurs HTTP admin (supplier-client.http.ts / campaign-client.http.ts).
 */
function readCsrfToken(): string | undefined {
  if (typeof document === 'undefined') {
    return undefined;
  }
  for (const part of document.cookie.split(';')) {
    const [rawName, ...rest] = part.trim().split('=');
    if (rawName === CSRF_COOKIE_NAME) {
      return decodeURIComponent(rest.join('='));
    }
  }
  return undefined;
}

/** Découpe un tableau en lots de taille ≤ `size` (chunking UI, borne POST summary). */
function chunk<T>(items: readonly T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
}

/** Adaptateur HTTP réel : `fetch` contre l'API admin campaign (résumé participations). */
export class HttpParticipationsClient implements ParticipationsClient {
  public constructor(private readonly baseUrl: string = '/api') {}

  public async summarizeParticipations(
    sirets: readonly string[],
  ): Promise<readonly SupplierParticipationSummaryDto[]> {
    if (sirets.length === 0) {
      return [];
    }
    // Chunking (GATE D) : chaque lot respecte la borne serveur ; fusion des résultats.
    const batches = await Promise.all(
      chunk(sirets, MAX_SUMMARY_SIRETS).map((batch) => this.postSummary(batch)),
    );
    return batches.flat();
  }

  public async getOpenCampaignFlag(
    siret: string,
  ): Promise<SupplierOpenCampaignFlagDto> {
    // Lecture seule (GET) : cookie de session admin via credentials, PAS de CSRF (double-submit réservé
    // aux mutations, parité HttpSupplierClient.get). L'autorité (périmètre « ouverte » PROTECTEUR) est
    // l'API — cet adaptateur ne fait que relayer le flag et son message d'erreur serveur.
    const response = await fetch(
      `${this.baseUrl}/admin/campaigns/participations/by-siret/${encodeURIComponent(siret)}/open-campaign`,
      {
        credentials: 'include',
        method: 'GET',
      },
    );
    if (!response.ok) {
      throw await HttpParticipationsClient.toApiError(response);
    }
    return (await response.json()) as SupplierOpenCampaignFlagDto;
  }

  public async getParticipationsBySiret(
    siret: string,
  ): Promise<SupplierParticipationsBySiretDto> {
    // Lecture seule (GET) : cookie de session admin via credentials, PAS de CSRF (double-submit réservé
    // aux mutations, parité getOpenCampaignFlag). L'autorité (tri « plus récente d'abord », RBAC
    // AdminSessionGuard) est l'API — cet adaptateur relaie la chronologie et son message d'erreur.
    const response = await fetch(
      `${this.baseUrl}/admin/campaigns/participations/by-siret/${encodeURIComponent(siret)}`,
      {
        credentials: 'include',
        method: 'GET',
      },
    );
    if (!response.ok) {
      throw await HttpParticipationsClient.toApiError(response);
    }
    return (await response.json()) as SupplierParticipationsBySiretDto;
  }

  private async postSummary(
    sirets: readonly string[],
  ): Promise<readonly SupplierParticipationSummaryDto[]> {
    const headers: Record<string, string> = {
      'content-type': 'application/json',
    };
    const csrf = readCsrfToken();
    if (csrf !== undefined) {
      headers['x-csrf-token'] = csrf;
    }
    const response = await fetch(
      `${this.baseUrl}/admin/campaigns/participations/summary`,
      {
        credentials: 'include',
        method: 'POST',
        headers,
        body: JSON.stringify({ sirets }),
      },
    );
    if (!response.ok) {
      throw await HttpParticipationsClient.toApiError(response);
    }
    return (await response.json()) as readonly SupplierParticipationSummaryDto[];
  }

  /** Convertit une réponse non-OK en ParticipationsApiError typée (message serveur tel quel). */
  private static async toApiError(
    response: Response,
  ): Promise<ParticipationsApiError> {
    let body: StructuredErrorBody | undefined;
    try {
      body = (await response.json()) as StructuredErrorBody;
    } catch {
      body = undefined;
    }
    const message =
      body?.message ??
      `Erreur API (${String(response.status)}) sur les participations.`;
    return new ParticipationsApiError(message, response.status);
  }
}
